import React, { useState, useMemo } from 'react';
import { usePayPal, PayPalSortField, SortDirection } from '../contexts/PayPalContext';
import { PayPalTransaction } from '../../../types/paypal';

const ITEMS_PER_PAGE = 50;

interface SortHeaderProps {
  field: PayPalSortField;
  label: string;
  currentField: PayPalSortField;
  currentDirection: SortDirection;
  onSort: (field: PayPalSortField) => void;
  className?: string;
}

function SortHeader({ field, label, currentField, currentDirection, onSort, className }: SortHeaderProps) {
  const isActive = currentField === field;
  return (
    <th 
      className={`cursor-pointer user-select-none ${className || ''}`}
      onClick={() => onSort(field)}
    >
      <div className="d-flex align-items-center gap-1">
        {label}
        {isActive && (
          <i className={`bi bi-chevron-${currentDirection === 'asc' ? 'up' : 'down'} fs-8`}></i>
        )}
      </div>
    </th>
  );
}

function TransactionRow({ txn }: { txn: PayPalTransaction }) {
  const { unmatchTransaction, ignoreTransaction } = usePayPal();
  const [actionLoading, setActionLoading] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number, currency = 'AUD') => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handleUnmatch = async () => {
    setActionLoading(true);
    try {
      await unmatchTransaction(txn.transaction_id);
    } finally {
      setActionLoading(false);
    }
  };

  const handleIgnore = async () => {
    setActionLoading(true);
    try {
      await ignoreTransaction(txn.transaction_id);
    } finally {
      setActionLoading(false);
    }
  };

  const isCredit = txn.amount > 0;
  const statusBadge = () => {
    switch (txn.reconciliation_status) {
      case 'matched':
        return <span className="badge badge-light-success">Matched</span>;
      case 'ignored':
        return <span className="badge badge-light-secondary">Ignored</span>;
      default:
        return <span className="badge badge-light-warning">Unmatched</span>;
    }
  };

  return (
    <tr>
      <td>
        <div className="d-flex flex-column">
          <span className="fw-semibold text-gray-800">{formatDate(txn.transaction_date)}</span>
        </div>
      </td>
      <td>
        <div className="d-flex flex-column">
          <span className="fw-semibold text-gray-800">{txn.payer_name || '-'}</span>
          <span className="text-muted fs-7">{txn.payer_email || '-'}</span>
        </div>
      </td>
      <td>
        <div className="d-flex flex-column">
          <span className="text-gray-800 text-truncate" style={{ maxWidth: '200px' }}>
            {txn.transaction_subject || txn.transaction_note || '-'}
          </span>
          {txn.invoice_id && (
            <span className="text-muted fs-7">Invoice: {txn.invoice_id}</span>
          )}
        </div>
      </td>
      <td className="text-end">
        <span className={`fw-bold ${isCredit ? 'text-success' : 'text-danger'}`}>
          {isCredit ? '+' : ''}{formatCurrency(txn.amount, txn.currency)}
        </span>
        {txn.fee_amount > 0 && (
          <div className="text-muted fs-8">Fee: {formatCurrency(txn.fee_amount, txn.currency)}</div>
        )}
      </td>
      <td className="text-end">
        <span className="fw-semibold text-gray-800">
          {formatCurrency(txn.net_amount, txn.currency)}
        </span>
      </td>
      <td className="text-center">{statusBadge()}</td>
      <td className="text-end">
        <div className="d-flex justify-content-end gap-1">
          {txn.reconciliation_status === 'unmatched' && (
            <button
              className="btn btn-sm btn-light-secondary"
              onClick={handleIgnore}
              disabled={actionLoading}
              title="Ignore transaction"
            >
              <i className="bi bi-eye-slash"></i>
            </button>
          )}
          {txn.reconciliation_status === 'matched' && (
            <button
              className="btn btn-sm btn-light-warning"
              onClick={handleUnmatch}
              disabled={actionLoading}
              title="Unmatch"
            >
              <i className="bi bi-x-circle"></i>
            </button>
          )}
          <button
            className="btn btn-sm btn-light"
            title="View details"
            onClick={() => {
              // Could open a modal with full transaction details
              console.log('Transaction details:', txn);
            }}
          >
            <i className="bi bi-eye"></i>
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function PayPalTransactionList() {
  const {
    filteredTransactions,
    transactionsLoading,
    sortField,
    sortDirection,
    setSorting,
    searchQuery,
    setSearchQuery,
    transactionTypeFilter,
    setTransactionTypeFilter,
    filters,
    setFilters,
  } = usePayPal();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTransactions, currentPage]);
  
  // Reset to page 1 when filters change
  const handleFilterChange = (newFilters: any) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  const handleSort = (field: PayPalSortField) => {
    setCurrentPage(1); // Reset to first page on sort
    if (sortField === field) {
      setSorting(field, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSorting(field, 'desc');
    }
  };

  const handleDateRangeChange = (range: string) => {
    const now = new Date();
    let from: string | undefined;
    let to: string | undefined = now.toISOString();
    
    switch (range) {
      case '7d':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '30d':
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '90d':
        from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'year':
        from = new Date(now.getFullYear(), 0, 1).toISOString();
        break;
      case 'all':
      default:
        from = undefined;
        to = undefined;
    }
    
    setCurrentPage(1);
    setFilters({ ...filters, from, to });
  };

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <h3 className="card-label fw-bold">PayPal Transactions</h3>
        </div>
        <div className="card-toolbar">
          {/* Search */}
          <div className="d-flex align-items-center position-relative me-4">
            <i className="bi bi-search position-absolute ms-3 fs-6 text-muted"></i>
            <input
              type="text"
              className="form-control form-control-sm form-control-solid ps-10"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => { setCurrentPage(1); setSearchQuery(e.target.value); }}
              style={{ width: '200px' }}
            />
          </div>

          {/* Transaction Type Filter */}
          <div className="me-4">
            <select
              className="form-select form-select-sm form-select-solid"
              value={transactionTypeFilter}
              onChange={(e) => { setCurrentPage(1); setTransactionTypeFilter(e.target.value as any); }}
            >
              <option value="all">All Transactions</option>
              <option value="credit">Money In</option>
              <option value="debit">Money Out</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="me-4">
            <select
              className="form-select form-select-sm form-select-solid"
              onChange={(e) => handleDateRangeChange(e.target.value)}
              defaultValue="30d"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="year">This year</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Reconciliation Status Filter */}
          <div>
            <select
              className="form-select form-select-sm form-select-solid"
              value={filters.reconciliationStatus || ''}
              onChange={(e) => { setCurrentPage(1); setFilters({ ...filters, reconciliationStatus: e.target.value as any || undefined }); }}
            >
              <option value="">All Status</option>
              <option value="unmatched">Unmatched</option>
              <option value="matched">Matched</option>
              <option value="ignored">Ignored</option>
            </select>
          </div>
        </div>
      </div>
      <div className="card-body pt-0">
        {transactionsLoading ? (
          <div className="d-flex justify-content-center py-10">
            <span className="spinner-border text-primary" role="status" />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-10 text-muted">
            <i className="bi bi-inbox fs-1 d-block mb-3"></i>
            No transactions found
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted bg-light">
                    <SortHeader
                      field="transaction_date"
                      label="Date"
                      currentField={sortField}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                      className="min-w-100px"
                    />
                    <SortHeader
                      field="payer_name"
                      label="From / To"
                      currentField={sortField}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                      className="min-w-150px"
                    />
                    <th className="min-w-150px">Description</th>
                    <SortHeader
                      field="amount"
                      label="Amount"
                      currentField={sortField}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                      className="text-end min-w-100px"
                    />
                    <th className="text-end min-w-100px">Net</th>
                    <th className="text-center min-w-80px">Status</th>
                    <th className="text-end min-w-80px">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((txn) => (
                    <TransactionRow key={txn.transaction_id} txn={txn} />
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-5">
              <div className="text-muted fs-7">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredTransactions.length)} of {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
              </div>
              
              {totalPages > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  
                  <span className="mx-3 fw-semibold">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
